import React from 'react';
import useSWR from 'swr';
import {useHistory, useLocation} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {isBefore} from 'date-fns';
import {Controller, useFormContext} from 'react-hook-form';

import i18n from 'i18n';
import api, {getURL} from 'api';
import SessionStorage from '../../utils/storage/SessionStorage';
import useMixPanel from 'hooks/useMixPanel';
import {useReservation} from 'context/reservation';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {resetPersonalDataFormStorage} from './utils';
import {PhoneInputRef} from '../acommon/FieldPhoneInput/PhoneInput';
import {Reservation, SelectOptionType} from 'utils/types';
import {useErrorModal, useIsMounted, usePrevious} from 'utils/hooks';
import {
  getHasGuestLeader,
  getHousingCountryCode,
  getIsCaptureStatFieldsEnabled,
  getStatTypeIfStatActive,
  getHasAnyPoliceCompleteGuest,
  getIsContractEnabled,
  getHousingManagerOrigin,
  getHasSuperHog,
} from 'utils/reservation';
import documents, {getDocTypes, getShouldResetDocTypes} from 'utils/docTypes';
import {
  COUNTRIES_WITH_PURPOSES_OF_STAY,
  COUNTRY_CODES,
  // DOC_TYPES_WITH_FRONT_AND_BACK_SIDES_SCAN,
  EU_MEMBERS,
  GENDERS_OPTIONS,
  PATTERNS,
  STAT_TYPES,
  STAT_TYPES_WITH_TAX_EXEMPTIONS,
  GROUP_TYPES,
  ORIGINS,
  GUEST_APP_EVENTS_NAME,
  STORAGE_KEYS,
} from 'utils/constants';
import {getFinishCheckinHistoryArgs} from 'utils/checkin';
import {useRegisterGuest} from 'hooks/useRegisterGuest';
import {getCurrentLocale} from 'utils/common';
import {setExperiencesModalAlreadyShowed} from 'utils/guests';
import {LocationStates} from '../../views/VerifyModalScreen/VerifyModalScreen';
import {PATHS} from '../../Routes';
import dataIncompleteIcon from 'assets/data-incomplete-icon.svg';
import loadingSpinner from 'assets/img/loading-spinner.gif';
import Input from 'components/acommon/FieldInput';
import Select from 'components/acommon/FieldSelect';
import AsyncSelect from 'components/acommon/FieldAsyncSelect';
import Datepicker from 'components/acommon/FieldDatepicker';
import PhoneInput from 'components/acommon/FieldPhoneInput';
import TimePicker from 'components/acommon/FieldTimePicker';
import FileInput from 'components/acommon/FieldFileInput';
import Button from 'components/acommon/Button';
import Modal from '../Modal';
import SignBlock from 'components/SignBlock';
import ModalGuestRegistered from './ModalGuestRegistered';
import {SignBlockRef} from '../SignBlock/SignBlock';
import {FormFieldWrapper} from 'styled/common';
import {
  Form,
  IncompleteModal,
  Fields,
  SendingDataModal,
  InvalidFileTypeError,
} from './styled';
import styles from './AddPersonalDataForm.module.sass';
import {
  LockVendors,
  FieldTypes,
  LocationType,
  PurposeOfStayType,
  DistrictType,
  MunicipalityType,
  CityType,
  TaxExemptionType,
  ProvincesType,
  CustomField,
  CustomForm,
  FORM_NAMES,
  EXTRA_FORM_NAMES,
  FormTypes,
  RequiredFields,
  FieldsDataTypes,
  LocationStatePersonalForm,
} from './types';

export const maxNamesInputLength = 30;
const acceptableDocuments = '.jpg, .png, .pdf, .jpeg';
const validTypes = ['image/jpeg', 'application/pdf', 'image/png'];

export function getLocationsAsOptions(data: {results: Array<LocationType>}) {
  const results = data?.results;

  if (!results) {
    return [];
  }

  return results.map(l => {
    return {
      value: l?.country?.code,
      label: l?.country?.name,
    };
  });
}

function getPurposesOfStayAsOptions(data: Array<PurposeOfStayType>) {
  if (!data) {
    return [];
  }
  return data.map(p => {
    return {
      value: p.id,
      label: p.name,
    };
  });
}

function getDistrictsAsOptions(data: {results: Array<DistrictType>}) {
  const districts = data?.results;

  if (!districts) {
    return [];
  }
  return districts.map(d => {
    return {
      value: d?.division_level_1?.code,
      label: d?.division_level_1?.name,
    };
  });
}

function getMunicipalitiesAsOptions(data: {results: Array<MunicipalityType>}) {
  const municipalities = data?.results;

  if (!municipalities) {
    return [];
  }
  return municipalities.map(m => {
    return {
      value: m?.division_level_2?.code,
      label: m?.division_level_2?.name,
    };
  });
}

function getCitiesAsOptions(data: {results: Array<CityType>}) {
  const results = data?.results;

  if (!results) {
    return [];
  }
  return results.map(l => {
    return {
      value: l?.division_level_3?.code,
      label: l?.division_level_3?.name,
    };
  });
}

function getTaxExemptionsAsOptions(data: Array<TaxExemptionType>) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(t => {
    return {
      value: t.id,
      label: t.name,
    };
  });
}

function getProvincesAsOptions(provinces: ProvincesType) {
  if (!provinces?.results) {
    return [];
  }

  return provinces?.results?.map(c => {
    return {
      label: c?.division_level_2?.name,
      value: c?.division_level_2?.code,
    };
  });
}

export const getBestMatches = (
  arr: Array<{label: string; value: string}>,
  inputValue: string,
) => {
  return arr
    .filter((x: {label: string; value: string}) =>
      x.label.toLowerCase().includes(inputValue.toLowerCase()),
    )
    .slice(0, 50)
    .sort((wo, yo) => {
      const w = wo.label.toLowerCase();
      const y = yo.label.toLowerCase();
      if (w.startsWith(inputValue) && y.startsWith(inputValue)) {
        return w > y ? 1 : -1;
      }
      if (w.startsWith(inputValue)) {
        return -1;
      }
      if (y.startsWith(inputValue)) {
        return 1;
      }
      return w > y ? 1 : -1;
    });
};

function validateDocTypes(number = '', type = '') {
  const DNI_STRING = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const DNI_DIVISION_NUMBER = 23;

  if (!PATTERNS.dniAndDl.test(number)) {
    return i18n.t('doc_number_dni_format_error');
  }

  const extractedNumbers = Number(number.slice(0, -1));
  const extractedChar = number.substr(number.length - 1).toUpperCase();

  const expectedChar = DNI_STRING[extractedNumbers % DNI_DIVISION_NUMBER];

  if (expectedChar !== extractedChar) {
    if (type === documents.types.drivingLicenseES) {
      return i18n.t('doc_number_dl_letter_error');
    }

    return i18n.t('doc_number_dni_letter_error');
  }

  return true;
}

function validateNIE(number = '') {
  const NIE_STRING = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const NIE_DIVISION_NUMBER = 23;
  const firstLetterValue: {[key: string]: string} = {
    X: '0',
    Y: '1',
    Z: '2',
  };

  if (!PATTERNS.nie.test(number)) {
    return i18n.t('doc_number_nie_format_error');
  }

  const withoutLetters = number.slice(1, -1);
  const lastSymbol = number[number.length - 1].toUpperCase();

  const firstLetter: string = number[0].toUpperCase();
  const replacedFirstLetter = firstLetterValue[firstLetter];

  if (replacedFirstLetter === undefined) {
    return i18n.t('doc_number_nie_letter_error');
  }

  const numberWithReplacedFirstLetter = Number(replacedFirstLetter + withoutLetters);
  const expectedChar = NIE_STRING[numberWithReplacedFirstLetter % NIE_DIVISION_NUMBER];

  if (lastSymbol !== expectedChar) {
    return i18n.t('doc_number_nie_letter_error');
  }

  return true;
}

function validateDocNumber(number = '', docType?: SelectOptionType | null) {
  const type = docType?.value;

  if (type && documents.dniValidation.includes(String(type))) {
    return validateDocTypes(number);
  }

  if (type && documents.nieValidation.includes(String(type))) {
    return validateNIE(number);
  }

  return true;
}

function getIsBirthDateBeforeIssueDate(birthDate: Date | null, issueDate: Date | null) {
  if (birthDate && issueDate) {
    return isBefore(new Date(birthDate), new Date(issueDate));
  }
  return true;
}

function getIsDateBeforeToday(date: Date) {
  return isBefore(new Date(date), new Date());
}

function validateBirthDate(birthDate: Date, issueDate: Date | null) {
  let error = '';
  const isBeforeIssueDate = getIsBirthDateBeforeIssueDate(birthDate, issueDate);
  const isBeforeToday = getIsDateBeforeToday(birthDate);

  if (!isBeforeIssueDate) {
    error = i18n.t('cant_be_equal_or_after_than_issue_date');
  }
  if (!isBeforeToday) {
    error += ` ${i18n.t('cant_be_equal_or_greater_than_today')}`;
  }

  return error || true;
}

function validateIssueDate(issueDate: Date, birthDate: Date | null) {
  let error = '';
  if (issueDate) {
    const isBeforeIssueDate = getIsBirthDateBeforeIssueDate(birthDate, issueDate);
    const isBeforeToday = getIsDateBeforeToday(issueDate);

    if (!isBeforeIssueDate) {
      error = i18n.t('cant_be_equal_or_smaller_than_birth_date');
    }
    if (!isBeforeToday) {
      error += ` ${i18n.t('cant_be_equal_or_greater_than_today')}`;
    }
  }

  return error || true;
}

function fetchPurposesOfStayIfRequired(reservation: Reservation) {
  const countryCode = getHousingCountryCode(reservation);
  const isAddingGuestLeader = !getHasGuestLeader(reservation);

  if (
    (countryCode === COUNTRY_CODES.czech && isAddingGuestLeader) ||
    COUNTRIES_WITH_PURPOSES_OF_STAY.includes(countryCode)
  ) {
    return getURL(api.purposesOfStay.ENDPOINTS.get(`country=${countryCode}`));
  }
  return null;
}

function fetchProvincesIfRequired(reservation: Reservation) {
  const countryCode = getHousingCountryCode(reservation);
  const isCaptureStatFieldsEnabled = getIsCaptureStatFieldsEnabled(reservation);
  if (countryCode === COUNTRY_CODES.spain && isCaptureStatFieldsEnabled) {
    return getURL(api.locations.ENDPOINTS.get(`country=${countryCode}&ordering=name`));
  }
  return null;
}

function fetchDistrictsIfRequired(reservation: Reservation) {
  const countryCode = getHousingCountryCode(reservation);

  if (countryCode === COUNTRY_CODES.colombia) {
    return getURL(
      api.locations.ENDPOINTS.get(
        `country=${countryCode}&division_level=1&ordering=name`,
      ),
    );
  }
  return null;
}

function fetchMunicipalitiesIfRequired(
  reservation: Reservation,
  district: string | number = '',
) {
  const countryCode = getHousingCountryCode(reservation);

  if (countryCode === COUNTRY_CODES.colombia) {
    return getURL(
      api.locations.ENDPOINTS.get(
        `country=${countryCode}&division_level=2&department=${district}&ordering=name`,
      ),
    );
  }
  return null;
}

function fetchCitiesIfRequired(reservation: Reservation) {
  const countryCode = getHousingCountryCode(reservation);

  if (countryCode === COUNTRY_CODES.italy) {
    return getURL(
      api.locations.ENDPOINTS.get(
        `country=${countryCode}&division_level=3&ordering=name`,
      ),
    );
  }
  return null;
}

function fetchTaxExemptionsIfRequired(reservation: Reservation) {
  const countryCode = getHousingCountryCode(reservation);
  const statType = getStatTypeIfStatActive(reservation);

  if (
    countryCode === COUNTRY_CODES.italy &&
    STAT_TYPES_WITH_TAX_EXEMPTIONS.includes(statType)
  ) {
    return getURL(api.statTaxExemptions.ENDPOINTS.get(statType));
  }
  return null;
}

function fetchCustomFormIfRequired(customFormId: string) {
  if (!customFormId) {
    return null;
  }

  return getURL(api.guestForms.ENDPOINTS.customFormById(customFormId));
}

export function getNamePattern(country?: string) {
  if (country === COUNTRY_CODES.italy) {
    return PATTERNS.nameWithoutAccents;
  }

  return PATTERNS.name;
}

function countRows(fieldsNumber?: number) {
  if (!fieldsNumber) {
    return 1;
  }

  return Math.ceil(fieldsNumber / 2);
}

function getCustomFieldData(field: CustomField) {
  const language = getCurrentLocale().toUpperCase();
  const defaultLabel = Object.values(field.names?.[0])?.[0] || '';
  const localeLabel = field.names.find(name => {
    return name[language];
  })?.[language];
  const defaultPlaceholder = field.placeholders?.length
    ? Object.values(field.placeholders?.[0])?.[0]
    : '';
  const localePlaceholder = field?.placeholders?.length
    ? field?.placeholders?.find(placeholder => {
        return placeholder[language];
      })?.[language]
    : '';

  const label = localeLabel || defaultLabel;
  const placeholder = localePlaceholder || defaultPlaceholder;
  const name = field.name;

  return {label, placeholder, name};
}

const DefaultRequiredLabel = i18n.t<string>('required');

const INIT_REQUIRED_FIELDS: RequiredFields = {
  [FORM_NAMES.name]: DefaultRequiredLabel,
  [FORM_NAMES.surname]: DefaultRequiredLabel,
  [FORM_NAMES.secondSurname]: false,
  [FORM_NAMES.sex]: DefaultRequiredLabel,
  [FORM_NAMES.birthDate]: DefaultRequiredLabel,
  [FORM_NAMES.nationality]: DefaultRequiredLabel,
  [FORM_NAMES.docType]: DefaultRequiredLabel,
  [FORM_NAMES.docNumber]: DefaultRequiredLabel,
  [FORM_NAMES.docDateOfIssue]: DefaultRequiredLabel,
  [FORM_NAMES.birthCountry]: DefaultRequiredLabel,
  [FORM_NAMES.residenceCountry]: DefaultRequiredLabel,
  [FORM_NAMES.residenceCity]: DefaultRequiredLabel,
  [FORM_NAMES.countryOfIssue]: DefaultRequiredLabel,
  [FORM_NAMES.cityOfBirth]: DefaultRequiredLabel,
  [FORM_NAMES.cityOfIssue]: DefaultRequiredLabel,
  [FORM_NAMES.residenceAddress]: DefaultRequiredLabel,
  [FORM_NAMES.nextDestinationCountry]: DefaultRequiredLabel,
  [FORM_NAMES.nextDestinationCity]: DefaultRequiredLabel,
  [FORM_NAMES.nextDestinationAddress]: DefaultRequiredLabel,
  [FORM_NAMES.residencePostalCode]: DefaultRequiredLabel,
  [FORM_NAMES.citizenship]: DefaultRequiredLabel,
  [FORM_NAMES.purposeOfStay]: DefaultRequiredLabel,
  [FORM_NAMES.visaNumber]: DefaultRequiredLabel,
  [FORM_NAMES.arrivalCountry]: DefaultRequiredLabel,
  [FORM_NAMES.arrivalDistrict]: DefaultRequiredLabel,
  [FORM_NAMES.arrivalMunicipality]: DefaultRequiredLabel,
  [FORM_NAMES.nextDestinationDistrict]: DefaultRequiredLabel,
  [FORM_NAMES.nextDestinationMunicipality]: DefaultRequiredLabel,
  [FORM_NAMES.taxExemption]: DefaultRequiredLabel,
  [FORM_NAMES.residenceProvince]: DefaultRequiredLabel,
  [FORM_NAMES.phone]: false,
  [FORM_NAMES.fiscalCode]: DefaultRequiredLabel,
  [EXTRA_FORM_NAMES.signature]: DefaultRequiredLabel,
};

function getRequiredFieldsByCountry({
  isPoliceActivated,
  reservation,
  nationalityCode,
  docType,
  residenceCountry,
  isSaltoLock,
}: FieldsDataTypes) {
  const countryCode = getHousingCountryCode(reservation);
  const isContractEnabled = reservation?.housing?.is_contract_enabled;
  const guestGroupType = reservation?.guest_group?.type;
  const isAddingGuestLeader = !getHasGuestLeader(reservation);

  switch (countryCode) {
    case COUNTRY_CODES.uk: {
      let fields: RequiredFields = {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.nextDestinationAddress]: false,
        [FORM_NAMES.phone]: Boolean(isSaltoLock) ? DefaultRequiredLabel : false,
      };

      if (nationalityCode === COUNTRY_CODES.uk) {
        fields = {
          ...fields,
          [FORM_NAMES.docNumber]: false,
          [FORM_NAMES.docType]: false,
        };
      }
      return fields;
    }
    case COUNTRY_CODES.belgium: {
      return {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.residenceAddress]: false,
        [FORM_NAMES.residenceCity]: false,
      };
    }
    case COUNTRY_CODES.france: {
      return {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.cityOfBirth]: false,
      };
    }
    case COUNTRY_CODES.germany: {
      return {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.phone]: Boolean(isSaltoLock) ? DefaultRequiredLabel : false,
      };
    }
    case COUNTRY_CODES.portugal: {
      return {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.birthCountry]: false,
      };
    }
    case COUNTRY_CODES.spain: {
      if (isPoliceActivated && nationalityCode === COUNTRY_CODES.spain) {
        return {
          ...INIT_REQUIRED_FIELDS,
          [FORM_NAMES.secondSurname]: DefaultRequiredLabel,
        };
      }
      return INIT_REQUIRED_FIELDS;
    }
    case COUNTRY_CODES.italy: {
      if (isContractEnabled) {
        return {
          ...INIT_REQUIRED_FIELDS,
          [FORM_NAMES.phone]: Boolean(isSaltoLock) ? DefaultRequiredLabel : false,
        };
      }
      return INIT_REQUIRED_FIELDS;
    }
    case COUNTRY_CODES.greece: {
      return {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.cityOfIssue]: false,
        [FORM_NAMES.email]: DefaultRequiredLabel,
        [FORM_NAMES.phone]: DefaultRequiredLabel,
      };
    }
    case COUNTRY_CODES.thailand: {
      if (isAddingGuestLeader) {
        return {
          ...INIT_REQUIRED_FIELDS,
          [FORM_NAMES.email]: DefaultRequiredLabel,
          [FORM_NAMES.phone]: DefaultRequiredLabel,
        };
      }

      return {
        ...INIT_REQUIRED_FIELDS,
      };
    }

    case COUNTRY_CODES.austria: {
      let fields = {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.email]: false,
        [FORM_NAMES.phone]: Boolean(isSaltoLock) ? DefaultRequiredLabel : false,
        [FORM_NAMES.docType]:
          residenceCountry === COUNTRY_CODES.austria ? false : DefaultRequiredLabel,
        [FORM_NAMES.docNumber]:
          residenceCountry === COUNTRY_CODES.austria && !docType
            ? false
            : DefaultRequiredLabel,
        [FORM_NAMES.docDateOfIssue]:
          residenceCountry === COUNTRY_CODES.austria ? false : DefaultRequiredLabel,
        [FORM_NAMES.countryOfIssue]:
          residenceCountry === COUNTRY_CODES.austria ? false : DefaultRequiredLabel,
        [FORM_NAMES.residencePostalCode]: [
          COUNTRY_CODES.austria,
          COUNTRY_CODES.germany,
        ].includes(residenceCountry as string)
          ? DefaultRequiredLabel
          : false,
      };

      if (guestGroupType === GROUP_TYPES.family) {
        if (!isAddingGuestLeader) {
          return {
            ...fields,
            [FORM_NAMES.docType]: false,
            [FORM_NAMES.docNumber]: docType ? DefaultRequiredLabel : false,
            [FORM_NAMES.residenceCity]: false,
            [FORM_NAMES.residenceAddress]: false,
          };
        } else {
          return {
            ...fields,
            [FORM_NAMES.docType]:
              residenceCountry === COUNTRY_CODES.austria ? false : DefaultRequiredLabel,
            [FORM_NAMES.docNumber]:
              residenceCountry === COUNTRY_CODES.austria && !docType
                ? false
                : DefaultRequiredLabel,
            [FORM_NAMES.docDateOfIssue]:
              residenceCountry === COUNTRY_CODES.austria ? false : DefaultRequiredLabel,
            [FORM_NAMES.countryOfIssue]:
              residenceCountry === COUNTRY_CODES.austria ? false : DefaultRequiredLabel,
          };
        }
      }

      return fields;
    }
    case COUNTRY_CODES.croatia: {
      return {
        ...INIT_REQUIRED_FIELDS,
        [FORM_NAMES.residenceAddress]: false,
        [FORM_NAMES.cityOfBirth]: false,
      };
    }
    default: {
      return INIT_REQUIRED_FIELDS;
    }
  }
}

function getRequiredFieldsByOrigin(reservation: Reservation): RequiredFields {
  const origin = getHousingManagerOrigin(reservation);
  switch (origin) {
    case ORIGINS.eviivo: {
      return {
        [FORM_NAMES.surname]: DefaultRequiredLabel,
        [FORM_NAMES.email]: DefaultRequiredLabel,
      };
    }

    default: {
      return {};
    }
  }
}

function getRequiredBySuperHub(reservation: Reservation) {
  const hasSuperHub = getHasSuperHog(reservation);

  if (hasSuperHub) {
    return {
      [FORM_NAMES.residencePostalCode]: DefaultRequiredLabel,
      [FORM_NAMES.residenceAddress]: DefaultRequiredLabel,
      [FORM_NAMES.residenceCountry]: DefaultRequiredLabel,
      [FORM_NAMES.residenceCity]: DefaultRequiredLabel,
      [FORM_NAMES.email]: DefaultRequiredLabel,
      [FORM_NAMES.phone]: DefaultRequiredLabel,
    };
  }
}

function getRequiredFields(reservationAndFieldsData: FieldsDataTypes) {
  const requiredFieldsByCountry = getRequiredFieldsByCountry(reservationAndFieldsData);
  const requiredFieldsByHousingOrigin = getRequiredFieldsByOrigin(
    reservationAndFieldsData.reservation,
  );

  const requiredBySuperHub = getRequiredBySuperHub(reservationAndFieldsData.reservation);

  return {
    ...requiredFieldsByCountry,
    ...requiredFieldsByHousingOrigin,
    ...requiredBySuperHub,
  };
}

const INIT_DISPLAY_FIELDS = {
  [FORM_NAMES.italianCityOfBirth]: false,
  [FORM_NAMES.italianCityOfIssue]: false,
  [FORM_NAMES.italianResidenceCity]: false,
  [FORM_NAMES.name]: true,
  [FORM_NAMES.sex]: true,
  [FORM_NAMES.birthDate]: true,
  [FORM_NAMES.nationality]: true,
  [FORM_NAMES.surname]: true,
  [FORM_NAMES.docType]: true,
  [FORM_NAMES.docNumber]: true,
  [FORM_NAMES.residenceCountry]: true,
  [FORM_NAMES.email]: true,
  [FORM_NAMES.docDateOfIssue]: false,
  [FORM_NAMES.secondSurname]: false,
  [FORM_NAMES.birthCountry]: false,
  [FORM_NAMES.residenceCity]: false,
  [FORM_NAMES.residenceAddress]: false,
  [FORM_NAMES.countryOfIssue]: false,
  [FORM_NAMES.cityOfBirth]: false,
  [FORM_NAMES.cityOfIssue]: false,
  [FORM_NAMES.nextDestinationCountry]: false,
  [FORM_NAMES.nextDestinationCity]: false,
  [FORM_NAMES.nextDestinationAddress]: false,
  [FORM_NAMES.residencePostalCode]: false,
  [FORM_NAMES.citizenship]: false,
  [FORM_NAMES.purposeOfStay]: false,
  [FORM_NAMES.visaNumber]: false,
  [FORM_NAMES.arrivalCountry]: false,
  [FORM_NAMES.arrivalDistrict]: false,
  [FORM_NAMES.arrivalMunicipality]: false,
  [FORM_NAMES.nextDestinationDistrict]: false,
  [FORM_NAMES.nextDestinationMunicipality]: false,
  [FORM_NAMES.taxExemption]: false,
  [FORM_NAMES.residenceProvince]: false,
  [FORM_NAMES.phone]: true,
  [FORM_NAMES.fiscalCode]: false,
};

function getDisplayFields({
  reservation,
  nationalityCode,
  formFields,
  isCustomForm,
}: FieldsDataTypes) {
  const housingCountryCode = getHousingCountryCode(reservation);
  const isAddingGuestLeader = !getHasGuestLeader(reservation);
  const hasAnyPoliceCompleteGuest = getHasAnyPoliceCompleteGuest(reservation);
  const guestGroupType = reservation?.guest_group?.type;
  const isContractEnabled = getIsContractEnabled(reservation);
  const hasSuperHub = getHasSuperHog(reservation);

  let fields = {
    ...INIT_DISPLAY_FIELDS,
  };

  if (hasSuperHub) {
    fields = {
      ...fields,
      [FORM_NAMES.residencePostalCode]: true,
      [FORM_NAMES.residenceAddress]: true,
      [FORM_NAMES.residenceCountry]: true,
      [FORM_NAMES.residenceCity]: true,
    };
  }
  let extraFields: Partial<Record<FORM_NAMES | EXTRA_FORM_NAMES, boolean>> = {
    [EXTRA_FORM_NAMES.signature]: true,
  };

  switch (housingCountryCode) {
    case COUNTRY_CODES.spain: {
      const isCaptureStatFieldsEnabled = getIsCaptureStatFieldsEnabled(reservation);

      fields = {
        ...fields,
        [FORM_NAMES.secondSurname]: true,
        [FORM_NAMES.residenceCountry]: false,
        [FORM_NAMES.docDateOfIssue]: true,
      };

      if (isCaptureStatFieldsEnabled) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.residenceCountry]: true,
        };

        if (formFields?.residenceCountry === COUNTRY_CODES.spain) {
          extraFields = {
            ...extraFields,
            [FORM_NAMES.residenceCity]: true,
            [FORM_NAMES.residenceProvince]: true,
          };
        }
      }

      break;
    }
    case COUNTRY_CODES.portugal: {
      fields = {
        ...fields,
        [FORM_NAMES.birthCountry]: true,
        [FORM_NAMES.countryOfIssue]: true,
        [FORM_NAMES.sex]: false,
      };

      break;
    }
    case COUNTRY_CODES.italy: {
      const statType = getStatTypeIfStatActive(reservation);
      fields = {
        ...fields,
        [FORM_NAMES.birthCountry]: true,
        [FORM_NAMES.residenceCountry]: false,
        [FORM_NAMES.docType]: false,
        [FORM_NAMES.docNumber]: false,
        [FORM_NAMES.countryOfIssue]: false,
      };

      extraFields = {
        ...extraFields,
        [EXTRA_FORM_NAMES.signature]: false,
      };

      if (isContractEnabled && isAddingGuestLeader) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.residenceCountry]: true,
          [FORM_NAMES.residenceCity]: true,
          [FORM_NAMES.residenceAddress]: true,
          [FORM_NAMES.residencePostalCode]: true,
          [EXTRA_FORM_NAMES.signature]: true,
        };

        if (nationalityCode === COUNTRY_CODES.italy) {
          extraFields = {
            ...extraFields,
            [FORM_NAMES.fiscalCode]: true,
          };
        }
      }

      if (statType) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.residenceCountry]: true,
        };

        if (formFields?.residenceCountry === COUNTRY_CODES.italy) {
          extraFields = {
            ...extraFields,
            [FORM_NAMES.italianResidenceCity]: true,
            [FORM_NAMES.residenceCity]: false,
          };
        }

        if (statType === STAT_TYPES.valtellinaAbit) {
          extraFields = {
            ...extraFields,
            [FORM_NAMES.taxExemption]: true,
          };
        }
      }

      if (formFields?.birthCountry === COUNTRY_CODES.italy) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.italianCityOfBirth]: true,
        };
      }

      if (isAddingGuestLeader || hasAnyPoliceCompleteGuest) {
        if (isAddingGuestLeader) {
          fields = {
            ...fields,
            [FORM_NAMES.docType]: true,
            [FORM_NAMES.docNumber]: true,
            [FORM_NAMES.countryOfIssue]: true,
          };
        } else {
          extraFields = {
            ...extraFields,
            [FORM_NAMES.docType]: true,
            [FORM_NAMES.docNumber]: true,
            [FORM_NAMES.countryOfIssue]: true,
          };
        }

        if (formFields?.countryOfIssue === COUNTRY_CODES.italy) {
          extraFields = {
            ...extraFields,
            [FORM_NAMES.italianCityOfIssue]: true,
          };
        }
      }

      break;
    }
    case COUNTRY_CODES.germany: {
      fields = {
        ...fields,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
        [FORM_NAMES.purposeOfStay]: true,
      };

      break;
    }
    case COUNTRY_CODES.uae: {
      if (isContractEnabled && isAddingGuestLeader) {
        fields = {
          ...fields,
          [FORM_NAMES.sex]: false,
          [FORM_NAMES.residenceCountry]: false,
        };
        extraFields = {
          ...extraFields,
          [EXTRA_FORM_NAMES.signature]: true,
        };

        break;
      }

      fields = {
        ...fields,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.residenceCountry]: false,
      };

      extraFields = {
        ...extraFields,
        [EXTRA_FORM_NAMES.signature]: false,
      };

      break;
    }
    case COUNTRY_CODES.netherlands: {
      fields = {
        ...fields,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
      };

      break;
    }
    case COUNTRY_CODES.belgium: {
      fields = {
        ...fields,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
      };

      break;
    }
    case COUNTRY_CODES.france: {
      fields = {
        ...fields,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.birthCountry]: true,
        [FORM_NAMES.cityOfBirth]: true,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
        [FORM_NAMES.docType]: false,
        [FORM_NAMES.docNumber]: false,
      };

      break;
    }
    case COUNTRY_CODES.uk: {
      fields = {
        ...fields,
        [FORM_NAMES.countryOfIssue]: true,
        [FORM_NAMES.nextDestinationCountry]: true,
        [FORM_NAMES.nextDestinationAddress]: true,
        [FORM_NAMES.nextDestinationCity]: true,
        [FORM_NAMES.residenceCountry]: false,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.birthDate]: false,
      };

      if (nationalityCode === COUNTRY_CODES.uk) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.countryOfIssue]: false,
          [FORM_NAMES.nextDestinationCountry]: false,
          [FORM_NAMES.nextDestinationCity]: false,
          [FORM_NAMES.nextDestinationAddress]: false,
        };
      }

      break;
    }
    case COUNTRY_CODES.austria: {
      fields = {
        ...fields,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
        [FORM_NAMES.residencePostalCode]: true,
        [FORM_NAMES.countryOfIssue]: true,
        [FORM_NAMES.docDateOfIssue]: true,
        [FORM_NAMES.email]: true,
      };

      if (guestGroupType === GROUP_TYPES.family && !isAddingGuestLeader) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.sex]: false,
          [FORM_NAMES.nationality]: false,
          [FORM_NAMES.docDateOfIssue]: false,
          [FORM_NAMES.countryOfIssue]: false,
          [FORM_NAMES.residenceAddress]: false,
          [EXTRA_FORM_NAMES.signature]: false,
        };
      }

      break;
    }
    case COUNTRY_CODES.czech: {
      fields = {
        ...fields,
        [FORM_NAMES.citizenship]: true,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
        [FORM_NAMES.sex]: false,
      };

      if (isAddingGuestLeader) {
        fields = {
          ...fields,
          [FORM_NAMES.purposeOfStay]: true,
        };
      }

      if (
        formFields?.citizenship &&
        !EU_MEMBERS.includes(String(formFields.citizenship))
      ) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.visaNumber]: true,
        };
      }

      break;
    }
    case COUNTRY_CODES.colombia: {
      fields = {
        ...fields,
        [FORM_NAMES.secondSurname]: true,
        [FORM_NAMES.arrivalCountry]: true,
        [FORM_NAMES.nextDestinationCountry]: true,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.residenceCountry]: false,
      };

      if (formFields?.arrivalCountry === COUNTRY_CODES.colombia) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.arrivalDistrict]: true,
          [FORM_NAMES.arrivalMunicipality]: true,
        };
      }

      if (formFields?.nextDestinationCountry === COUNTRY_CODES.colombia) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.arrivalDistrict]: true,
          [FORM_NAMES.arrivalMunicipality]: true,
          [FORM_NAMES.nextDestinationDistrict]: true,
          [FORM_NAMES.nextDestinationMunicipality]: true,
        };
      }

      break;
    }
    case COUNTRY_CODES.romania: {
      fields = {
        ...fields,
        [FORM_NAMES.sex]: false,
        [FORM_NAMES.birthCountry]: true,
        [FORM_NAMES.cityOfBirth]: true,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
        [FORM_NAMES.purposeOfStay]: true,
      };

      break;
    }
    case COUNTRY_CODES.thailand: {
      fields = {
        ...fields,
        [FORM_NAMES.residenceCountry]: false,
      };
      extraFields = {
        ...extraFields,
        [EXTRA_FORM_NAMES.signature]: false,
      };

      if (isContractEnabled && isAddingGuestLeader) {
        extraFields = {
          ...extraFields,
          [EXTRA_FORM_NAMES.signature]: true,
        };
      }

      break;
    }
    case COUNTRY_CODES.greece: {
      fields = {
        ...fields,
        [FORM_NAMES.countryOfIssue]: true,
        [FORM_NAMES.docDateOfIssue]: true,
        [FORM_NAMES.residencePostalCode]: true,
        [FORM_NAMES.residenceAddress]: true,
        [FORM_NAMES.residenceCity]: true,
      };

      if (!isAddingGuestLeader) {
        extraFields = {
          ...extraFields,
          [EXTRA_FORM_NAMES.signature]: false,
        };
      }

      if (formFields?.countryOfIssue === COUNTRY_CODES.greece) {
        extraFields = {
          ...extraFields,
          [FORM_NAMES.cityOfIssue]: true,
        };
      }

      break;
    }
    case COUNTRY_CODES.croatia: {
      fields = {
        ...fields,
        [FORM_NAMES.residenceCity]: true,
        [FORM_NAMES.residenceAddress]: true,
        [FORM_NAMES.birthCountry]: true,
        [FORM_NAMES.cityOfBirth]: true,
        [FORM_NAMES.citizenship]: true,
        [FORM_NAMES.nationality]: false,
        [FORM_NAMES.email]: false,
      };
      extraFields = {
        ...extraFields,
        [EXTRA_FORM_NAMES.signature]: false,
      };

      if (isContractEnabled && isAddingGuestLeader) {
        extraFields = {
          ...extraFields,
          [EXTRA_FORM_NAMES.signature]: true,
        };
      }

      break;
    }
    default: {
    }
  }

  if (hasSuperHub) {
    fields = {
      ...fields,
      [FORM_NAMES.residencePostalCode]: true,
      [FORM_NAMES.residenceAddress]: true,
      [FORM_NAMES.residenceCountry]: true,
      [FORM_NAMES.residenceCity]: true,
    };
  }

  if (isCustomForm) {
    return extraFields;
  }

  return {...fields, ...extraFields};
}

export function getFields(data: FieldsDataTypes) {
  const display = getDisplayFields(data);
  const required: RequiredFields = getRequiredFields(data);

  return {display, required};
}

export const getRequiredOrOptionalFieldLabelCallback = (
  label: string,
  required: string | boolean,
) => {
  if (required) {
    return label;
  }

  return `${label} (${i18n.t('optional')})`;
};

function AddPersonalDataForm() {
  const {t, i18n} = useTranslation();
  const history = useHistory();
  const location = useLocation<LocationStatePersonalForm>();
  const isMounted = useIsMounted();
  const registerGuest = useRegisterGuest();
  const {ErrorModal, displayError, closeErrorModal} = useErrorModal();
  const {
    isDubaiReservation,
    isThailandReservation,
    isItalyReservation,
    hasGuestMembers,
    hasBiomatchForGuestLeader,
    shortCustomForm,
    isPoliceActivated,
    paymentsDetails,
    ...reservationDetails
  } = useComputedReservationDetails();

  const {data: reservation, patchReservation, refreshReservation} = useReservation();
  const {
    handleSubmit,
    register,
    trigger,
    setValue,
    formState,
    watch,
    getValues,
    control,
    reset,
  } = useFormContext<FormTypes>();
  const {errors} = formState;
  const prevFormState = usePrevious<{isSubmitted: boolean; isValid: boolean}>(formState);
  const name = watch(FORM_NAMES.name);
  const surname = watch(FORM_NAMES.surname);
  const sex = watch(FORM_NAMES.sex)?.value as string;
  const docType = watch(FORM_NAMES.docType)?.value as string;
  const birthDate = watch(FORM_NAMES.birthDate);
  const docDateOfIssue = watch(FORM_NAMES.docDateOfIssue);
  const birthCountry = watch(FORM_NAMES.birthCountry)?.value as string;
  const countryOfIssue = watch(FORM_NAMES.countryOfIssue)?.value as string;
  const nationality = watch(FORM_NAMES.nationality)?.value as string;
  const citizenship = watch(FORM_NAMES.citizenship)?.value as string;
  const arrivalCountry = watch(FORM_NAMES.arrivalCountry)?.value as string;
  const nextDestinationCountry = watch(FORM_NAMES.nextDestinationCountry)
    ?.value as string;
  const arrivalMunicipality = watch(FORM_NAMES.arrivalMunicipality)?.value;
  const arrivalDistrict = watch(FORM_NAMES.arrivalDistrict)?.value;
  const nextDestinationMunicipality = watch(
    FORM_NAMES.nextDestinationMunicipality,
  )?.value;
  const residenceCountry = watch(FORM_NAMES.residenceCountry)?.value as string;
  const defaultPhoneDetails = getValues()?.[EXTRA_FORM_NAMES.phoneDetails];
  const phoneInputRef = React.useRef<PhoneInputRef>(null);

  const hasReservationId = Boolean(reservation.id);
  const {data: locks} = useSWR(
    hasReservationId
      ? getURL(api.locks.ENDPOINTS.get(`unlock_links__reservation_id=${reservation.id}`))
      : null,
  );
  const isSaltoLock = Boolean(
    locks?.some?.((lock: any) => lock.vendor === LockVendors.salto),
  );

  const [fields, setFields] = React.useState(() => {
    return getFields({
      reservation,
      isSaltoLock,
      docType,
      residenceCountry,
      isPoliceActivated,
      nationalityCode: nationality,
      isCustomForm: false,
      formFields: {
        [FORM_NAMES.birthCountry]: birthCountry,
        [FORM_NAMES.countryOfIssue]: countryOfIssue,
        [FORM_NAMES.citizenship]: citizenship,
        [FORM_NAMES.arrivalCountry]: arrivalCountry,
        [FORM_NAMES.nextDestinationCountry]: nextDestinationCountry,
        [FORM_NAMES.residenceCountry]: residenceCountry,
        [FORM_NAMES.docType]: docType,
      },
    });
  });
  const [docTypes, setDocTypes] = React.useState(() => {
    return getDocTypes(reservation, nationality, {residenceCountry});
  });

  const signatureRef = React.useRef<SignBlockRef>(null);

  const {data: locations, mutate: revalidateLocations} = useSWR(
    getURL(api.locations.ENDPOINTS.get('ordering=name')),
  );
  const locationsAsOptions = React.useMemo(() => {
    return getLocationsAsOptions(locations);
  }, [locations]);
  const {data: districts} = useSWR(fetchDistrictsIfRequired(reservation));
  const {data: arrivalMunicipalities} = useSWR(
    fetchMunicipalitiesIfRequired(reservation, arrivalDistrict),
  );
  const {data: taxExemptions} = useSWR(fetchTaxExemptionsIfRequired(reservation));
  const {data: provinces} = useSWR(fetchProvincesIfRequired(reservation));
  const provincesAsOptions = React.useMemo(() => {
    return getProvincesAsOptions(provinces);
  }, [provinces]);
  const taxExemptionsAsOptions = React.useMemo(() => {
    return getTaxExemptionsAsOptions(taxExemptions);
  }, [taxExemptions]);
  const prevArrivalMunicipalities = usePrevious(arrivalMunicipalities);
  const arrivalMunicipalitiesAsOptions = React.useMemo(() => {
    return getMunicipalitiesAsOptions(arrivalMunicipalities);
  }, [arrivalMunicipalities]);
  const nextDestinationDistrict = watch(FORM_NAMES.nextDestinationDistrict)?.value;
  const {data: nextDestinationMunicipalities} = useSWR(
    fetchMunicipalitiesIfRequired(reservation, nextDestinationDistrict),
  );
  const prevNextDestinationMunicipalities = usePrevious(nextDestinationMunicipalities);
  const nextDestinationMunicipalitiesAsOptions = React.useMemo(() => {
    return getMunicipalitiesAsOptions(nextDestinationMunicipalities);
  }, [nextDestinationMunicipalities]);
  const {data: cities} = useSWR(fetchCitiesIfRequired(reservation));
  const {data: purposesOfStay} = useSWR(fetchPurposesOfStayIfRequired(reservation));
  const citiesAsOptions = React.useMemo(() => {
    return getCitiesAsOptions(cities);
  }, [cities]);

  const [isDataIncompleteModalOpen, setIsDataIncompleteModalOpen] = React.useState(false);
  const [wasDataIncompleteModalOpened, setWasDataIncompleteModalOpened] =
    React.useState(false);
  const [isSignModalOpen, setIsSignModalOpen] = React.useState(false);
  const [isLoadingModalOpen, setIsLoadingModalOpen] = React.useState(false);
  const [isModalGuestRegisteredOpen, setModalGuestRegisteredOpen] = React.useState(false);
  const [isGuestSuccessfulCreated, setGuestSuccessfulCreated] = React.useState(false);
  const [isDisabledRedirects, setIsDisabledRedirects] = React.useState(false);
  const fullNameCurrentGuestRef = React.useRef('');
  const canRevalidateForm =
    formState.isSubmitted && !formState.isSubmitSuccessful && !formState.isSubmitting;

  const isGuestLeader = !getHasGuestLeader(reservation);
  const customFormId = shortCustomForm?.id || '';
  const {data: customForm, isValidating: isValidatingCustomForm} = useSWR<CustomForm>(
    fetchCustomFormIfRequired(customFormId),
  );
  const isLoadingCustomForm = !customForm && isValidatingCustomForm;
  const hasCustomForm = Boolean(customForm?.id);
  const {IdGuestAppEvents} = useMixPanel();
  const [isInvalidFileType, setIsInvalidFileType] = React.useState(false);

  React.useLayoutEffect(
    function redirectToBack() {
      const {hasBiomatchRequiredCurrentGuest, hasScanDocumentRequiredCurrentGuest} =
        reservationDetails;
      const identityVerificationState =
        location.state?.[LocationStates.identityVerificationState];

      if (
        (hasBiomatchRequiredCurrentGuest || hasScanDocumentRequiredCurrentGuest) &&
        !identityVerificationState?.isIdentityVerificationFinished
      ) {
        history.push(PATHS.verificationStart);
      }
    },
    [i18n.language, sex, setValue, reservationDetails, location.state, history],
  );

  React.useLayoutEffect(
    function resirectIfPaymentsRequired() {
      const hasRequiredPayment =
        hasGuestMembers && reservationDetails.hasAnyMandatoryPayment;
      if (hasRequiredPayment && !isDisabledRedirects) {
        const redirectPathname =
          !paymentsDetails?.deposit.paid && paymentsDetails?.deposit.required
            ? PATHS.protect
            : PATHS.pay;

        history.push(redirectPathname, location.state);
      }
    },
    [
      reservationDetails.hasAnyMandatoryPayment,
      paymentsDetails?.deposit,
      isModalGuestRegisteredOpen,
      isDisabledRedirects,
      hasGuestMembers,
      location.state,
      history,
    ],
  );

  React.useEffect(
    function changeTranslationErrorMessages() {
      if (formState.isSubmitted && !formState.isSubmitSuccessful) {
        trigger();
      }
    },
    [formState.isSubmitted, formState.isSubmitSuccessful, i18n.language, trigger],
  );

  React.useEffect(
    function handleGenderOptionsLanguage() {
      const nextGenderOption = GENDERS_OPTIONS().find(option => option.value === sex);

      setValue(FORM_NAMES.sex, nextGenderOption);
    },
    [i18n.language, sex, setValue],
  );

  React.useEffect(
    function handleLanguageChange() {
      revalidateLocations();
    },
    [i18n.language, revalidateLocations],
  );

  React.useEffect(
    function updateLanguageOfFormValues() {
      const findCountry = (fieldName: string, countryCode: string) => {
        const {country} = locations.results.find(
          ({country}: {country: {code: string}}) => {
            return country.code === countryCode;
          },
        );

        if (country) {
          const {code, name} = country;

          setValue(fieldName, {value: code, label: name});
        }
      };

      if (!locations) return;

      if (residenceCountry) {
        findCountry(FORM_NAMES.residenceCountry, residenceCountry);
      }

      if (nationality) {
        findCountry(FORM_NAMES.nationality, nationality);
      }

      if (citizenship) {
        findCountry(FORM_NAMES.citizenship, citizenship);
      }

      if (birthCountry) {
        findCountry(FORM_NAMES.birthCountry, birthCountry);
      }

      if (countryOfIssue) {
        findCountry(FORM_NAMES.countryOfIssue, countryOfIssue);
      }

      if (arrivalCountry) {
        findCountry(FORM_NAMES.arrivalCountry, arrivalCountry);
      }

      if (nextDestinationCountry) {
        findCountry(FORM_NAMES.nextDestinationCountry, nextDestinationCountry);
      }
    },
    [
      locations,
      residenceCountry,
      nationality,
      citizenship,
      birthCountry,
      countryOfIssue,
      arrivalCountry,
      nextDestinationCountry,
      setValue,
    ],
  );

  React.useEffect(
    function setDisplayFieldsOnChange() {
      const nextFields = getFields({
        reservation,
        isSaltoLock,
        docType,
        residenceCountry,
        isPoliceActivated,
        nationalityCode: nationality,
        isCustomForm: hasCustomForm,
        formFields: {
          [FORM_NAMES.birthCountry]: birthCountry,
          [FORM_NAMES.countryOfIssue]: countryOfIssue,
          [FORM_NAMES.citizenship]: citizenship,
          [FORM_NAMES.arrivalCountry]: arrivalCountry,
          [FORM_NAMES.nextDestinationCountry]: nextDestinationCountry,
          [FORM_NAMES.residenceCountry]: residenceCountry,
          [FORM_NAMES.docType]: docType,
        },
      });
      setFields(nextFields);

      if (canRevalidateForm) {
        trigger();
      }
    },
    [
      reservation,
      nationality,
      canRevalidateForm,
      trigger,
      birthCountry,
      countryOfIssue,
      citizenship,
      arrivalCountry,
      nextDestinationCountry,
      residenceCountry,
      docType,
      hasCustomForm,
      isSaltoLock,
      isPoliceActivated,
    ],
  );

  React.useEffect(
    function revalidateFormOnDisplayFieldsChange() {
      if (canRevalidateForm) {
        trigger();
      }
    },
    [trigger, fields, canRevalidateForm],
  );

  const preloadPersistedStorageFormData = React.useCallback(() => {
    const storageFormData = SessionStorage.get<FormTypes>(STORAGE_KEYS.personalFormData);

    const persistedState =
      storageFormData || (location.state?.formData as Record<string, any>);
    if (persistedState) {
      const formData = Object.entries(persistedState).reduce<Record<string, any>>(
        (acc, [fieldName, fieldValue]) => {
          if (fieldValue?.type === FieldTypes.date) {
            acc[fieldName] = new Date(fieldValue.value);
          } else {
            acc[fieldName] = fieldValue;
          }
          return acc;
        },
        {},
      );

      reset(formData);
    }
  }, [location.state?.formData, reset]);

  React.useEffect(() => {
    preloadPersistedStorageFormData();
  }, [preloadPersistedStorageFormData]);

  const persisteFormDataToStorage = React.useCallback(() => {
    const isSignatureTouched = signatureRef.current?.isEnabled;
    const isFormTouched = formState.isDirty;
    const signature = signatureRef.current?.canvas?.toDataURL();
    const phoneDetails = phoneInputRef.current;

    if (formState.isSubmitSuccessful) return;

    if (isSignatureTouched) {
      SessionStorage.set(STORAGE_KEYS.personalSignatureFormData, signature);
    }
    if (isFormTouched) {
      const formData = Object.entries(getValues()).reduce<Record<string, any>>(
        (acc, [fieldName, fieldValue]) => {
          if (fieldValue instanceof Date) {
            acc[fieldName] = {value: fieldValue, type: FieldTypes.date};
          } else {
            acc[fieldName] = fieldValue;
          }
          return acc;
        },
        {},
      );

      SessionStorage.set(STORAGE_KEYS.personalFormData, {
        ...formData,
        [EXTRA_FORM_NAMES.phoneDetails]: phoneDetails,
      });
    }
  }, [formState.isSubmitSuccessful, formState.isDirty, getValues]);

  React.useEffect(() => {
    return () => {
      persisteFormDataToStorage();
    };
  }, [persisteFormDataToStorage]);

  // React.useEffect(
  //   function highlightFieldsManually() {
  //     const shouldHighlight = location.state?.highlightFormFields;
  //     if (shouldHighlight) {
  //       console.log('shouldHighlight', shouldHighlight);
  //       handleSubmit(() => {})();
  //     }
  //   },
  //   [location.state, handleSubmit],
  // );
  // console.log('highlightFormFields', location.state?.highlightFormFields);

  React.useEffect(
    function setDocTypesOnNationalityOrReservationChange() {
      const nextDocTypes = getDocTypes(reservation, nationality, {
        residenceCountry,
        citizenship,
      });
      const shouldResetDocType = getShouldResetDocTypes(
        nextDocTypes,
        getValues()[FORM_NAMES.docType],
      );

      if (shouldResetDocType) {
        setValue(FORM_NAMES.docType, undefined);
      }
      setDocTypes(nextDocTypes);
    },
    [citizenship, reservation, nationality, setValue, getValues, residenceCountry],
  );

  React.useEffect(
    function revalidateDocNumberOnDocTypeChange() {
      if (canRevalidateForm) {
        trigger(FORM_NAMES.docNumber);
      }
    },
    [docType, trigger, canRevalidateForm],
  );

  React.useEffect(
    function getDocTypesOnLanguageChange() {
      const nextDocTypes = getDocTypes(reservation, nationality, {
        residenceCountry,
        citizenship,
      });

      const nextDocValue = nextDocTypes.find(doc => doc.value === docType);

      setDocTypes(nextDocTypes);
      if (nextDocValue) {
        setValue(FORM_NAMES.docType, nextDocValue);
      }
    },
    [
      i18n.language,
      citizenship,
      residenceCountry,
      nationality,
      reservation,
      setValue,
      docType,
    ],
  );

  React.useLayoutEffect(
    function showIncompleteModalOneTime() {
      const showIncompleteModal =
        !wasDataIncompleteModalOpened &&
        !isModalGuestRegisteredOpen &&
        !isSignModalOpen &&
        formState.isSubmitted &&
        !formState.isSubmitSuccessful &&
        !prevFormState?.isValid &&
        !formState.isValid;

      if (showIncompleteModal) {
        setIsDataIncompleteModalOpen(true);
        setWasDataIncompleteModalOpened(true);
      }
    },
    [
      wasDataIncompleteModalOpened,
      isModalGuestRegisteredOpen,
      isSignModalOpen,
      formState.isSubmitted,
      formState.isSubmitSuccessful,
      formState.isValid,
      prevFormState,
    ],
  );

  React.useEffect(
    function resetArrivalMunicipalityIfNotFoundInNewOptions() {
      const isLoadingMunicipalities =
        arrivalMunicipality &&
        prevArrivalMunicipalities &&
        !arrivalMunicipalitiesAsOptions.length;

      if (isLoadingMunicipalities) {
        return;
      }

      const hasOption = arrivalMunicipalitiesAsOptions.some(m => {
        return m.value === arrivalMunicipality;
      });

      if (!hasOption) {
        setValue(FORM_NAMES.arrivalMunicipality, undefined);
      }
    },
    [
      setValue,
      arrivalMunicipalitiesAsOptions,
      arrivalMunicipality,
      prevArrivalMunicipalities,
    ],
  );

  React.useEffect(
    function resetNextDestinationMunicipalityIfNotFoundInNewOptions() {
      const isLoadingMunicipalities =
        nextDestinationMunicipality &&
        prevNextDestinationMunicipalities &&
        !nextDestinationMunicipalitiesAsOptions.length;

      if (isLoadingMunicipalities) {
        return;
      }

      const hasOption = nextDestinationMunicipalitiesAsOptions.some(m => {
        return m.value === nextDestinationMunicipality;
      });

      if (!hasOption) {
        setValue(FORM_NAMES.nextDestinationMunicipality, undefined);
      }
    },
    [
      setValue,
      nextDestinationMunicipalitiesAsOptions,
      nextDestinationMunicipality,
      prevNextDestinationMunicipalities,
    ],
  );

  React.useEffect(
    function revalidateDateFieldsOnChange() {
      if (canRevalidateForm) {
        trigger([FORM_NAMES.birthDate, FORM_NAMES.docDateOfIssue]);
      }
    },
    [trigger, canRevalidateForm, birthDate, docDateOfIssue],
  );

  const handleFileChange = (onChange: (...event: any[]) => void) => (event: any) => {
    if (!event) {
      return onChange(event);
    }

    const {target} = event;
    if (target?.files.length) {
      if (!validTypes.includes(target?.files[0].type)) {
        setIsInvalidFileType(true);
        return;
      }
      setIsInvalidFileType(false);
      return onChange(target.files[0]);
    }
  };

  const closeDataIncompleteModal = () => {
    setIsDataIncompleteModalOpen(false);
  };

  const getRequiredOrOptionalFieldLabel = React.useCallback(
    getRequiredOrOptionalFieldLabelCallback,
    [],
  );

  // const getPersistedState = (data: FormTypes) => {
  //   return {
  //     ...location.state,
  //     customFormId,
  //     formData: data,
  //     highlightFormFields: false,
  //   };
  // };

  const getComputedNumberOfGuests = () => {
    if (location.state?.children) {
      const numberGuestsWithChildren =
        Number(location.state?.number_of_guests) + Number(location.state.children);
      return numberGuestsWithChildren > 0 ? numberGuestsWithChildren : 0;
    }

    return location.state?.number_of_guests;
  };

  const getReservationPayload = () => {
    if (hasGuestMembers) {
      return null;
    }

    return {
      check_in_date: location.state?.updatedCheckInDate || undefined,
      check_out_date: location.state?.updatedCheckOutDate || undefined,
      guest_group: {
        number_of_guests: getComputedNumberOfGuests(),
      },
    };
  };

  const updateReservation = () => {
    const payload = getReservationPayload();
    if (payload) {
      return patchReservation(payload);
    }

    return {
      error: null,
      data: null,
    };
  };

  const finishCheckin = (registeredGuestName: string) => {
    const {url, state} = getFinishCheckinHistoryArgs({
      reservationDetails: {
        isDubaiReservation,
        isThailandReservation,
        isItalyReservation,
        hasGuestMembers,
        hasBiomatchForGuestLeader,
        shortCustomForm,
        isPoliceActivated,
        ...reservationDetails,
      },
      locationState: {
        registeredGuestName,
      },
    });

    history.push(url, state);
  };

  const resetCustomFields = React.useCallback(() => {
    if (customForm) {
      customForm.fields_set?.forEach(({field}) => {
        reset({...getValues(), [field.name]: null});
      });
    }
  }, [customForm, getValues, reset]);

  const resetFormData = React.useCallback(() => {
    resetPersonalDataFormStorage();

    reset({
      ...getValues(),
      [FORM_NAMES.name]: null,
      [FORM_NAMES.surname]: null,
      [FORM_NAMES.secondSurname]: '',
      [FORM_NAMES.sex]: null,
      [FORM_NAMES.birthDate]: null,
      [FORM_NAMES.nationality]: null,
      [FORM_NAMES.docType]: null,
      [FORM_NAMES.docNumber]: '',
      [FORM_NAMES.docDateOfIssue]: null,
      [FORM_NAMES.birthCountry]: null,
      [FORM_NAMES.residenceCountry]: null,
      [FORM_NAMES.cityOfBirth]: null,
      [FORM_NAMES.cityOfIssue]: null,
      [FORM_NAMES.countryOfIssue]: null,
      [FORM_NAMES.residenceAddress]: '',
      [FORM_NAMES.residenceCity]: null,
      [FORM_NAMES.nextDestinationCountry]: null,
      [FORM_NAMES.nextDestinationCity]: '',
      [FORM_NAMES.nextDestinationAddress]: '',
      [FORM_NAMES.residencePostalCode]: '',
      [FORM_NAMES.citizenship]: null,
      [FORM_NAMES.purposeOfStay]: null,
      [FORM_NAMES.visaNumber]: '',
      [FORM_NAMES.arrivalCountry]: null,
      [FORM_NAMES.arrivalDistrict]: null,
      [FORM_NAMES.arrivalMunicipality]: null,
      [FORM_NAMES.nextDestinationDistrict]: null,
      [FORM_NAMES.nextDestinationMunicipality]: null,
      [FORM_NAMES.taxExemption]: null,
      [FORM_NAMES.residenceProvince]: null,
      [FORM_NAMES.email]: '',
      [FORM_NAMES.phone]: null,
      [FORM_NAMES.fiscalCode]: null,
    });

    signatureRef.current?.reset();
    resetCustomFields();
  }, [getValues, reset, resetCustomFields]);

  React.useEffect(
    function finishCreateGuest() {
      if (formState.isSubmitSuccessful && isGuestSuccessfulCreated) {
        IdGuestAppEvents(GUEST_APP_EVENTS_NAME.registered);
        setIsLoadingModalOpen(false);
        setModalGuestRegisteredOpen(true);
        setGuestSuccessfulCreated(false);
        resetFormData();
      }
    },
    [
      formState.isSubmitSuccessful,
      isGuestSuccessfulCreated,
      resetFormData,
      IdGuestAppEvents,
    ],
  );

  const registerGuestAndUpdateReservation = async (
    data: FormTypes,
    signature: string,
  ) => {
    closeErrorModal();
    setIsLoadingModalOpen(true);

    const {error: reservationError} = await updateReservation();

    if (!reservationError && isMounted.current) {
      const {error: guestError} = await registerGuest({
        formData: data,
        guestData: {
          signature,
        },
      });
      // localStorage.removeItem('photosState');

      if (!guestError && isMounted.current) {
        const {error: refreshError} = await refreshReservation(() => {
          if (isMounted.current) {
            setGuestSuccessfulCreated(true);
          }
        });

        if (refreshError) {
          displayError(refreshError);
        }
      } else {
        displayError(guestError);
      }
    } else {
      displayError(reservationError);
    }

    if (isMounted.current) {
      setIsLoadingModalOpen(false);
    }
  };

  const asyncValidatePhone = async () => {
    const number = getValues()[FORM_NAMES.phone];
    if (!number) return null;
    const phone = number.trim();

    if (!phone) return null;
    const {error} = await api.validators.validatePhoneNumber({phone});
    return error;
  };

  const onSubmit = async (data: FormTypes, signature: string) => {
    const errorValidatePhone = await asyncValidatePhone();
    if (errorValidatePhone) return displayError(errorValidatePhone);
    // const persistedState = getPersistedState(data);
    // console.log('persistedState', persistedState);
    fullNameCurrentGuestRef.current = `${name} ${surname}`;

    let emptyMembeFromInit = localStorage.getItem('empty-member-from-init') === 'true';

    if (emptyMembeFromInit && hasGuestMembers) {
      IdGuestAppEvents(GUEST_APP_EVENTS_NAME.registerAnotherGuest);
    }

    setExperiencesModalAlreadyShowed(false);
    setIsDisabledRedirects(true);
    await registerGuestAndUpdateReservation(data, signature);
  };

  const formFields = React.useMemo(
    () => ({
      [FORM_NAMES.name]: (required = fields.required[FORM_NAMES.name]) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('name'), required)}
            placeholder={t('enter_name')}
            {...register(FORM_NAMES.name, {
              required,
              pattern: {
                value: getNamePattern(getHousingCountryCode(reservation)),
                message: t('cant_contain_number_and_symbols'),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            error={errors[FORM_NAMES.name]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.surname]: (required = fields.required[FORM_NAMES.surname]) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('surname'), required)}
            placeholder={t('enter_surname')}
            {...register(FORM_NAMES.surname, {
              required,
              pattern: {
                value: getNamePattern(getHousingCountryCode(reservation)),
                message: t('cant_contain_number_and_symbols'),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            error={errors[FORM_NAMES.surname]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.secondSurname]: (
        required = fields.required[FORM_NAMES.secondSurname],
      ) => {
        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(t('second_surname'), required)}
              placeholder={t('enter_second_surname')}
              {...register(FORM_NAMES.secondSurname, {
                required,
                pattern: {
                  value: getNamePattern(getHousingCountryCode(reservation)),
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              error={errors[FORM_NAMES.secondSurname]?.message}
              disabled={isLoadingCustomForm}
            />
          </FormFieldWrapper>
        );
      },

      [FORM_NAMES.sex]: (required = fields.required[FORM_NAMES.sex]) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.sex}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select_sex')}
                label={getRequiredOrOptionalFieldLabel(t('sex'), required)}
                options={GENDERS_OPTIONS()}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.nationality]: (required = fields.required[FORM_NAMES.nationality]) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.nationality}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select_nationality')}
                label={getRequiredOrOptionalFieldLabel(t('nationality'), required)}
                options={locationsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.citizenship]: (required = fields.required[FORM_NAMES.citizenship]) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.citizenship}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('citizenship'), required)}
                options={locationsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.birthDate]: (required = fields.required[FORM_NAMES.birthDate]) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.birthDate}
            rules={{
              required,
              validate: value => {
                if (!value) {
                  return true;
                }
                const docDateOfIssue = getValues()[FORM_NAMES.docDateOfIssue];
                return validateBirthDate(value, docDateOfIssue);
              },
            }}
            control={control}
            render={({field: {onChange, ...restField}, fieldState: {error}}) => (
              <Datepicker
                disabled={isLoadingCustomForm}
                error={error?.message}
                label={getRequiredOrOptionalFieldLabel(t('birth_date'), required)}
                onChange={value => {
                  return onChange(value?.toString());
                }}
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.birthCountry]: (
        required = fields.required[FORM_NAMES.birthCountry],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.birthCountry}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('country_of_birth'), required)}
                options={locationsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.cityOfBirth]: (required = fields.required[FORM_NAMES.cityOfBirth]) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.cityOfBirth, {
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('city_of_birth'), required)}
            placeholder={t('enter_city_of_birth')}
            error={errors[FORM_NAMES.cityOfBirth as string]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residenceCountry]: (
        required = fields.required[FORM_NAMES.residenceCountry],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.residenceCountry}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('residence_country'), required)}
                options={locationsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residenceProvince]: (
        required = fields.required[FORM_NAMES.residenceProvince],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.residenceProvince}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('residence_province'), required)}
                options={provincesAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residenceCity]: (
        required = fields.required[FORM_NAMES.residenceCity],
      ) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.residenceCity, {
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('residence_city'), required)}
            placeholder={t('enter_residence_city')}
            error={errors[FORM_NAMES.residenceCity as string]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residenceAddress]: (
        required = fields.required[FORM_NAMES.residenceAddress],
      ) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.residenceAddress, {
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('residence_address'), required)}
            placeholder={t('enter_residence_address')}
            error={errors[FORM_NAMES.residenceAddress]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.residencePostalCode]: (
        required = fields.required[FORM_NAMES.residencePostalCode],
      ) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('residence_postal_code'), required)}
            placeholder={t('enter_residence_postal_code')}
            {...register(FORM_NAMES.residencePostalCode, {
              required,
            })}
            error={errors[FORM_NAMES.residencePostalCode]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.docType]: (required = fields.required[FORM_NAMES.docType]) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.docType}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('doc_type'), required)}
                options={docTypes}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.docNumber]: (required = fields.required[FORM_NAMES.docNumber]) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('doc_number'), required)}
            placeholder={t('enter_doc_number')}
            {...register(FORM_NAMES.docNumber, {
              required,
              validate: value =>
                validateDocNumber(value, getValues()[FORM_NAMES.docType]),
              pattern: {
                value: PATTERNS.docNumber,
                message: t('only_basic_letters_are_allowed') as string,
              },
            })}
            error={errors[FORM_NAMES.docNumber]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),

      [FORM_NAMES.docDateOfIssue]: (
        required = fields.required[FORM_NAMES.docDateOfIssue],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.docDateOfIssue}
            rules={{
              required,
              validate: value => {
                if (!value) {
                  return true;
                }

                const birthDate = getValues()[FORM_NAMES.birthDate];
                return validateIssueDate(value, birthDate);
              },
            }}
            control={control}
            render={({field: {onChange, ...restField}, fieldState: {error}}) => (
              <Datepicker
                withTooltip
                tooltipContentKey={
                  <Trans i18nKey="date_when_the_doc_created">
                    Date when the document was created. (It is <b>not</b> the expiration
                    date)
                  </Trans>
                }
                onChange={value => {
                  return onChange(value?.toString());
                }}
                error={error?.message}
                disabled={isLoadingCustomForm}
                label={getRequiredOrOptionalFieldLabel(t('date_of_issue'), required)}
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.countryOfIssue]: (
        required = fields.required[FORM_NAMES.countryOfIssue],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.countryOfIssue}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('country_of_issue'), required)}
                options={locationsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.cityOfIssue]: (required = fields.required[FORM_NAMES.cityOfIssue]) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.cityOfIssue, {
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('city_of_issue'), required)}
            placeholder={t('enter_issue_city')}
            error={errors[FORM_NAMES.cityOfIssue as string]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.arrivalCountry]: (
        required = fields.required[FORM_NAMES.arrivalCountry],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.arrivalCountry}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(
                  t('arrived_from_country'),
                  required,
                )}
                options={locationsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.arrivalDistrict]: (
        required = fields.required[FORM_NAMES.arrivalDistrict],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.arrivalDistrict}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(
                  t('arrived_from_district'),
                  required,
                )}
                options={getDistrictsAsOptions(districts)}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.arrivalMunicipality]: (
        required = fields.required[FORM_NAMES.arrivalMunicipality],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.arrivalMunicipality}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(
                  t('arrived_from_municipality'),
                  required,
                )}
                options={arrivalMunicipalitiesAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.nextDestinationCountry]: (
        required = fields.required[FORM_NAMES.nextDestinationCountry],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.nextDestinationCountry}
            rules={{
              required,
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(
                  t('next_destination_country'),
                  required,
                )}
                options={locationsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.nextDestinationCity]: (
        required = fields.required[FORM_NAMES.nextDestinationCity],
      ) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.nextDestinationCity, {
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('next_destination_city'), required)}
            placeholder={t('enter_next_destination_city')}
            error={errors[FORM_NAMES.nextDestinationCity]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.nextDestinationAddress]: (
        required = fields.required[FORM_NAMES.nextDestinationAddress],
      ) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              t('next_destination_address'),
              required,
            )}
            placeholder={t('enter_next_destination_address')}
            {...register(FORM_NAMES.nextDestinationAddress, {
              required,
            })}
            error={errors[FORM_NAMES.nextDestinationAddress]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.nextDestinationDistrict]: (
        required = fields.required[FORM_NAMES.nextDestinationDistrict],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.nextDestinationDistrict}
            rules={{
              required,
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(
                  t('next_destination_district'),
                  required,
                )}
                options={getDistrictsAsOptions(districts)}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.nextDestinationMunicipality]: (
        required = fields.required[FORM_NAMES.nextDestinationMunicipality],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.nextDestinationMunicipality}
            rules={{
              required,
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(
                  t('next_destination_municipality'),
                  required,
                )}
                options={nextDestinationMunicipalitiesAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.purposeOfStay]: (
        required = fields.required[FORM_NAMES.purposeOfStay],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.purposeOfStay}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('purpose_of_stay'), required)}
                options={getPurposesOfStayAsOptions(purposesOfStay)}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.visaNumber]: (required = fields.required[FORM_NAMES.visaNumber]) => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.visaNumber, {
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('visa_number_eu'), required)}
            inputMode="numeric"
            placeholder={t('enter_visa_number')}
            error={errors[FORM_NAMES.visaNumber]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.taxExemption]: (
        required = fields.required[FORM_NAMES.taxExemption],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.taxExemption}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                placeholder={t('select')}
                label={getRequiredOrOptionalFieldLabel(t('tax_exemption'), required)}
                options={taxExemptionsAsOptions}
                error={error?.message}
                disabled={isLoadingCustomForm}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.email]: (required = fields.required[FORM_NAMES.email]) => {
        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(t('email'), required)}
              placeholder={t('email')}
              {...register(FORM_NAMES.email, {
                required,
                pattern: {
                  value: PATTERNS.email,
                  message: t('invalid_email'),
                },
              })}
              inputMode="email"
              error={errors[FORM_NAMES.email]?.message}
              disabled={isLoadingCustomForm}
            />
          </FormFieldWrapper>
        );
      },
      [FORM_NAMES.phone]: (required = fields.required[FORM_NAMES.phone]) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.phone}
            rules={{
              required,
            }}
            control={control}
            render={({field: {ref, ...restField}, fieldState: {error}}) => (
              <PhoneInput
                defaultCode={defaultPhoneDetails?.code}
                defaultInputValue={defaultPhoneDetails?.number}
                label={getRequiredOrOptionalFieldLabel(t('phone_number'), required)}
                placeholder={t('enter_your_phone_number')}
                error={error?.message}
                disabled={isLoadingCustomForm}
                ref={phoneInputRef}
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.fiscalCode]: (required = fields.required[FORM_NAMES.fiscalCode]) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(t('fiscal_code'), required)}
            placeholder={t('fiscal_code')}
            {...register(FORM_NAMES.fiscalCode, {
              required,
              pattern: {
                value: PATTERNS.fiscalCode,
                message: t('invalid_fiscal_code'),
              },
            })}
            error={errors[FORM_NAMES.fiscalCode]?.message}
            disabled={isLoadingCustomForm}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.italianCityOfBirth]: (
        required = fields.required[FORM_NAMES.cityOfBirth],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.cityOfBirth}
            rules={{required}}
            control={control}
            render={({field: {value, ...restField}, fieldState: {error}}) => (
              <AsyncSelect
                placeholder={t('select')}
                loadOptions={(inputValue, callback) => {
                  callback(getBestMatches(citiesAsOptions, inputValue));
                }}
                label={getRequiredOrOptionalFieldLabel(t('city_of_birth'), required)}
                error={error?.message}
                disabled={isLoadingCustomForm}
                value={value as SelectOptionType}
                cacheOptions
                defaultValues
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.italianResidenceCity]: (
        required = fields.required[FORM_NAMES.residenceCity],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.residenceCity}
            control={control}
            rules={{required}}
            render={({field: {value, ...restField}, fieldState: {error}}) => (
              <AsyncSelect
                placeholder={t('select')}
                loadOptions={(inputValue, callback) => {
                  callback(getBestMatches(citiesAsOptions, inputValue));
                }}
                label={getRequiredOrOptionalFieldLabel(t('residence_city'), required)}
                error={error?.message}
                disabled={isLoadingCustomForm}
                value={value as SelectOptionType}
                cacheOptions
                defaultValues
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.italianCityOfIssue]: (
        required = fields.required[FORM_NAMES.cityOfIssue],
      ) => (
        <FormFieldWrapper>
          <Controller
            name={FORM_NAMES.cityOfIssue}
            rules={{required}}
            control={control}
            render={({field: {value, ...restField}, fieldState: {error}}) => (
              <AsyncSelect
                placeholder={t('select')}
                loadOptions={(inputValue, callback) => {
                  callback(getBestMatches(citiesAsOptions, inputValue));
                }}
                label={getRequiredOrOptionalFieldLabel(t('city_of_issue'), required)}
                error={error?.message}
                disabled={isLoadingCustomForm}
                value={value as SelectOptionType}
                cacheOptions
                defaultValues
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
    }),
    [
      arrivalMunicipalitiesAsOptions,
      citiesAsOptions,
      control,
      districts,
      docTypes,
      errors,
      fields.required,
      getRequiredOrOptionalFieldLabel,
      getValues,
      isLoadingCustomForm,
      locationsAsOptions,
      nextDestinationMunicipalitiesAsOptions,
      provincesAsOptions,
      purposesOfStay,
      register,
      reservation,
      defaultPhoneDetails?.code,
      defaultPhoneDetails?.number,
      t,
      taxExemptionsAsOptions,
    ],
  );

  const getCustomField = React.useCallback(
    (field: CustomField, required: boolean | string) => {
      const {name, placeholder, label} = getCustomFieldData(field);
      switch (field.field_type) {
        case FieldTypes.date:
          return (
            <FormFieldWrapper>
              <Controller
                name={name}
                rules={{required}}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <Datepicker
                    label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        case FieldTypes.time:
          return (
            <FormFieldWrapper>
              <Controller
                name={name}
                rules={{required}}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <TimePicker
                    placeholder={placeholder}
                    label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        case FieldTypes.file:
          return (
            <FormFieldWrapper>
              <Controller
                name={name}
                rules={{
                  required,
                }}
                control={control}
                render={({field: {onChange, ...restField}, fieldState: {error}}) => (
                  <FileInput
                    placeholder={placeholder}
                    onChange={handleFileChange(onChange)}
                    label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                    error={error?.message}
                    accept={acceptableDocuments}
                    invalid={isInvalidFileType}
                    {...restField}
                  />
                )}
              />
              {isInvalidFileType && (
                <InvalidFileTypeError>{t('only_doc_file_types')}</InvalidFileTypeError>
              )}
            </FormFieldWrapper>
          );
        default:
          return (
            <FormFieldWrapper>
              <Input
                id={name}
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                placeholder={placeholder}
                {...register(name, {
                  required,
                })}
                error={errors[name]?.message}
              />
            </FormFieldWrapper>
          );
      }
    },
    [control, errors, getRequiredOrOptionalFieldLabel, register, isInvalidFileType, t],
  );

  const filterCustomFormNames = React.useCallback(
    (formNames: FORM_NAMES[]) => {
      const customFormNames = customForm!.fields_set.map(({field, is_leaders_field}) => {
        if (!isGuestLeader && is_leaders_field) {
          return '';
        }

        return field.name;
      });

      return formNames.filter(formName => {
        return !customFormNames.includes(formName);
      });
    },
    [customForm, isGuestLeader],
  );

  const displayingFormNames = React.useMemo(() => {
    let formNames = Object.values(FORM_NAMES).filter(formName => {
      return fields.display[formName];
    });

    if (hasCustomForm) {
      formNames = filterCustomFormNames(formNames);
    }

    return formNames;
  }, [fields.display, filterCustomFormNames, hasCustomForm]);

  const renderFormFields = () => {
    return displayingFormNames.map(formName => {
      return <React.Fragment key={formName}>{formFields[formName]?.()}</React.Fragment>;
    });
  };

  const renderCustomFormFields = () => {
    const isAddingGuestLeader = !getHasGuestLeader(reservation);

    if (!customForm) {
      return null;
    }

    const fieldsSet = renderFormFields();

    const customFieldsSet = customForm.fields_set.map(
      ({field, is_required, is_leaders_field}) => {
        const required =
          (is_required || fields.required[field.name as FORM_NAMES]) &&
          (t('required') as string);
        const hidden = fields.display[field.name as FORM_NAMES] === false;

        if (hidden || (!isAddingGuestLeader && is_leaders_field)) {
          return null;
        }

        if (!field.is_custom) {
          const formField = formFields[field.name as FORM_NAMES];

          return <React.Fragment key={field.id}>{formField?.(required)}</React.Fragment>;
        }

        const customField = getCustomField(field, required);
        return <React.Fragment key={field.id}>{customField}</React.Fragment>;
      },
    );

    return [...customFieldsSet, ...fieldsSet];
  };

  const getRowsCount = () => {
    const fieldsNumber = displayingFormNames.length;
    const customFieldsNumber = customForm?.fields_set?.length || 0;

    return hasCustomForm
      ? countRows(customFieldsNumber + fieldsNumber)
      : countRows(fieldsNumber);
  };

  const rowsCount = getRowsCount();

  const handleSubmitForm = (signature: string) => {
    handleSubmit((data: FormTypes) => onSubmit(data, signature))();
  };

  return (
    <>
      <ErrorModal />
      <SendingDataModal
        open={isLoadingModalOpen}
        title={t('sending_your_data')}
        text={t('it_could_take_seconds')}
        iconSrc={loadingSpinner}
        iconProps={{width: 60, height: 60}}
        iconAlt="Person"
      />

      <Modal open={isModalGuestRegisteredOpen}>
        <ModalGuestRegistered
          setModalGuestRegisteredOpen={setModalGuestRegisteredOpen}
          finishCheckin={finishCheckin}
          name={fullNameCurrentGuestRef.current}
        />
      </Modal>

      <Modal
        closeOnDocumentClick
        closeOnEscape
        open={isDataIncompleteModalOpen}
        onClose={closeDataIncompleteModal}
      >
        <IncompleteModal>
          <img
            src={dataIncompleteIcon}
            alt="Form with a red field"
            width="84"
            height="84"
          />
          <div className="title">{t('incomplete_personal_data')}</div>
          <div className="text">{t('at_least_one_field_is_missing')}</div>
          <div className="modalButtons">
            <Button onClick={closeDataIncompleteModal}>{t('ok')}</Button>
          </div>
        </IncompleteModal>
      </Modal>
      <div className={styles.formWrapper}>
        <Form autoComplete="on" data-testid="guest-form">
          <Fields data-testid="fields" rowsCount={rowsCount}>
            {hasCustomForm ? renderCustomFormFields() : renderFormFields()}
          </Fields>
        </Form>
        <SignBlock
          ref={signatureRef}
          showSignArea={!!fields.display[EXTRA_FORM_NAMES.signature]}
          setIsSignModalOpen={setIsSignModalOpen}
          isSignModalOpen={isSignModalOpen}
          onSubmit={handleSubmitForm}
        />
      </div>
    </>
  );
}

export {AddPersonalDataForm};
